import React, { useState } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PublicIcon from '@mui/icons-material/Public';
import { salidasDeLote, validarRangosSalida, validarSalidasDeRango, resumenSalidasDesdeRangos } from "./salidasRangos";
import SalidasDelRango from "./SalidasDelRango";
import DocumentosDelRango from "./DocumentosDelRango";
import ModalDocumentosRangoHolograma, { subirDocumentosPendientesDeRangos, subirDocumentosPendientesDeSalidas } from "./ModalDocumentosRangoHolograma";
import ModalDocumentosSalidaHolograma from "./ModalDocumentosSalidaHolograma";
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Cada rango de hologramas del lote es una salida con su tipo de salida y su estatus (los guarda el servidor)
export const getParcialidadesForRecord = (rec) => salidasDeLote(rec);

// Helper para calcular inventarios de botellas y cajas de un lote terminado
export const calcRecordInventory = (rec) => {
  if (!rec) {
    return { entradaBot: 0, entradaCaj: 0, salidaBot: 0, salidaCaj: 0, saldoBot: 0, saldoCaj: 0, parcialidades: [] };
  }
  const porCaja = parseInt(String(rec.botCaja || rec.bot_caja || "12").replace(/\D/g, ""), 10) || 12;
  const entradaBot = Number(
    rec.entradaBotellas ||
    rec.entrada_botellas ||
    rec.invInicialBotellas ||
    rec.inv_inicial_botellas ||
    rec.totalHologramas ||
    0
  );
  const entradaCaj = Number(
    rec.entradaCajas ||
    rec.entrada_cajas ||
    rec.invInicialCajas ||
    rec.inv_inicial_cajas ||
    (entradaBot > 0 ? Math.floor(entradaBot / porCaja) : 0)
  );

  const parcialidades = salidasDeLote(rec);
  const vigentes = parcialidades.filter(p => p.estatus !== "rechazado");

  // Sumar ÚNICAMENTE las botellas que realmente se dieron de salida como producto terminado
  let salidaBot = 0;
  let haySalidasRegistradas = false;

  vigentes.forEach(p => {
    const subs = (p.salidas || []).filter(s => String(s.estatus || "").toLowerCase() !== "rechazado");
    if (subs.length > 0) {
      haySalidasRegistradas = true;
      salidaBot += subs.reduce((acc, s) => acc + (parseInt(s.cantidad, 10) || 0), 0);
    } else if (p.tipoSalida && p.tipoSalida.trim() !== "") {
      haySalidasRegistradas = true;
      salidaBot += Number(p.botellas || 0);
    }
  });

  if (!haySalidasRegistradas && vigentes.length === 0) {
    salidaBot = Number(rec.salidaBotellas || rec.salida_botellas || 0);
  }

  const salidaCaj = Math.floor(salidaBot / porCaja);
  const saldoBot = Math.max(0, entradaBot - salidaBot);
  const saldoCaj = Math.max(0, Math.floor(saldoBot / porCaja));

  return { entradaBot, entradaCaj, salidaBot, salidaCaj, saldoBot, saldoCaj, parcialidades };
};

// Inventario de hologramas del cliente: folio siguiente y saldo disponible
const limitesInventario = (inv, reservados = 0) => {
  if (!inv) return null;
  const pref = String(inv.prefijo || "A").trim().toUpperCase();
  const digitos = String(inv.rango_completo || `${inv.rango_inicial || ""} - ${inv.rango_final || ""}`).match(/\d+/g) || [];
  if (digitos.length === 0) return null;
  const minNum = parseInt(digitos[0], 10);
  const inicial = Number(inv.cantidad_inicial ?? (digitos.length > 1 ? parseInt(digitos[1], 10) - minNum + 1 : 0));
  const agotado = String(inv.estatus || "").toLowerCase() === "agotado";
  const disponibleServidor = agotado ? 0 : Math.max(0, Number(inv.cantidad_disponible ?? inicial));
  const usados = Math.max(0, inicial - disponibleServidor) + reservados; // aceptados + reservados por rangos pendientes
  const disponible = Math.max(0, disponibleServidor - reservados);
  return {
    id: inv.id_bitacora_hologramas || inv.id,
    prefijo: pref,
    rangoCompleto: inv.rango_completo || `${pref}${digitos[0]} - ${pref}${digitos[1] || digitos[0]}`,
    siguienteFolio: minNum + usados,
    padLength: Math.max(...digitos.map(d => d.length)),
    disponible
  };
};

const EnvasadoTerminadoSection = ({
  terminadoRecords = [],
  hologramaRecords = [],
  onUpdateTerminado,
  almacenes = []
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [rangoDocsModal, setRangoDocsModal] = useState({
    isOpen: false,
    rango: null,
    lote: null,
    rangosDelLote: []
  });
  const [salidaDocsModal, setSalidaDocsModal] = useState({
    isOpen: false,
    salida: null,
    rango: null,
    lote: null,
    salidasDelRango: []
  });

  // Modal «Agregar salida»: muestra TODOS los rangos del lote y permite agregar rangos nuevos del inventario del cliente
  // (siempre que no excedan las botellas del lote); cada rango se puede repartir en salidas opcionales con su tipo.
  const [isSalidaModalOpen, setIsSalidaModalOpen] = useState(false);
  const [selectedRecordForSalida, setSelectedRecordForSalida] = useState(null);
  const [guardandoSalida, setGuardandoSalida] = useState(false);
  const [draftRangos, setDraftRangos] = useState([]);
  const [salidaInvId, setSalidaInvId] = useState("");
  const [salidaBotellas, setSalidaBotellas] = useState("");
  const [salidaPrefijo, setSalidaPrefijo] = useState("A");
  const [salidaFolioIni, setSalidaFolioIni] = useState("");
  const [salidaFolioFin, setSalidaFolioFin] = useState("");
  const [salidaObs, setSalidaObs] = useState("");

  // Hologramas ya apartados por rangos pendientes de validación (aún no descontados en el inventario)
  const reservadosPorInventario = {};
  terminadoRecords.forEach(t => (t.rangosHologramas || []).forEach(r => {
    const invId = r.id_bitacora_hologramas || r.idInventario;
    if (invId && String(r.estatus || "").toLowerCase() === "pendiente") {
      reservadosPorInventario[invId] = (reservadosPorInventario[invId] || 0) + (Number(r.cantidad) || 0);
    }
  }));

  const inventariosConSaldo = hologramaRecords
    .map(inv => limitesInventario(inv, reservadosPorInventario[inv.id_bitacora_hologramas || inv.id] || 0))
    .filter(b => b && b.disponible > 0);

  // Cuentas del borrador del modal
  const entradaBotModal = selectedRecordForSalida ? calcRecordInventory(selectedRecordForSalida).entradaBot : 0;
  const asignadoDraft = draftRangos
    .filter(d => String(d.estatus || "").toLowerCase() !== "rechazado")
    .reduce((acc, d) => acc + (parseInt(d.cantidad, 10) || 0), 0);
  const saldoDraft = Math.max(0, entradaBotModal - asignadoDraft);
  const usadoNuevoInv = (invId) => draftRangos
    .filter(d => d.nuevo && String(d.id_bitacora_hologramas) === String(invId))
    .reduce((acc, d) => acc + (parseInt(d.cantidad, 10) || 0), 0);
  const boundsSel = inventariosConSaldo.find(x => String(x.id) === String(salidaInvId));
  const dispSel = boundsSel ? Math.max(0, boundsSel.disponible - usadoNuevoInv(salidaInvId)) : 0;

  const limpiarFormularioRango = () => {
    setSalidaInvId("");
    setSalidaBotellas("");
    setSalidaPrefijo("A");
    setSalidaFolioIni("");
    setSalidaFolioFin("");
    setSalidaObs("");
  };

  const handleOpenSalidaModal = (rec) => {
    setSelectedRecordForSalida(rec);
    setDraftRangos((rec.rangosHologramas || []).map((r, i) => ({
      ...r,
      key: `r-${r.id_producto_holograma || r.id || i}`,
      nuevo: false,
      rango1: r.rango1 ?? r.rango_inicial ?? "",
      rango2: r.rango2 ?? r.rango_final ?? "",
      estatus: String(r.estatus || "pendiente").toLowerCase(),
      observaciones: r.observaciones || "",
      salidas: (r.salidas || []).map(x => ({
        ...x,
        id_salida: x.id_salida || x.id,
        rango1: x.rango1 ?? x.rango_inicial ?? "",
        rango2: x.rango2 ?? x.rango_final ?? "",
        cantidad: String(x.cantidad ?? ""),
        tipo_salida: x.tipo_salida || "",
        estatus: String(x.estatus || "pendiente").toLowerCase()
      }))
    })));
    limpiarFormularioRango();
    setIsSalidaModalOpen(true);
  };

  const calcFolioFin = (ini, botellas) => {
    const a = parseInt(ini, 10);
    const b = parseInt(botellas, 10);
    if (isNaN(a) || isNaN(b) || b <= 0) return "";
    return String(a + b - 1).padStart(String(ini).length, "0");
  };

  const handleSelectInventario = (invId) => {
    setSalidaInvId(invId);
    const b = inventariosConSaldo.find(x => String(x.id) === String(invId));
    if (!b) {
      setSalidaFolioIni("");
      setSalidaFolioFin("");
      return;
    }
    // El siguiente folio libre considera los rangos nuevos que ya se agregaron del mismo inventario
    const ini = String(b.siguienteFolio + usadoNuevoInv(invId)).padStart(b.padLength, "0");
    setSalidaPrefijo(b.prefijo);
    setSalidaFolioIni(ini);
    setSalidaFolioFin(calcFolioFin(ini, salidaBotellas));
  };

  const handleSalidaBotellasChange = (val) => {
    setSalidaBotellas(val);
    if (salidaFolioIni) setSalidaFolioFin(calcFolioFin(salidaFolioIni, val));
  };

  const handleFolioIniChange = (val) => {
    setSalidaFolioIni(val);
    if (salidaBotellas) setSalidaFolioFin(calcFolioFin(val, salidaBotellas));
  };

  const folioIniNum = parseInt(salidaFolioIni, 10);
  const folioFinNum = parseInt(salidaFolioFin, 10);
  const foliosCount = (!isNaN(folioIniNum) && !isNaN(folioFinNum) && folioFinNum >= folioIniNum)
    ? (folioFinNum - folioIniNum + 1)
    : 0;
  const botellasNum = parseInt(salidaBotellas, 10) || 0;
  const isFoliosMatch = botellasNum > 0 && foliosCount === botellasNum;

  // Agrega un rango nuevo al borrador (todavía no se guarda en el servidor)
  const handleAgregarRangoDraft = () => {
    const warn = (title, text) => Swal.fire({ icon: "warning", title, text, confirmButtonColor: "#6B21A8" });
    if (!boundsSel) return warn("Inventario requerido", "Seleccione el lote de hologramas de su inventario.");
    if (botellasNum <= 0) return warn("Cantidad requerida", "Ingrese una cantidad válida de botellas mayor a 0.");
    if (botellasNum > saldoDraft) return warn("Saldo insuficiente", `El lote solo tiene ${saldoDraft} botellas sin asignar.`);
    if (botellasNum > dispSel) return warn("Saldo insuficiente en inventario", `El lote de hologramas ${boundsSel.rangoCompleto} solo tiene ${dispSel} folios disponibles.`);
    if (!salidaFolioIni.trim() || !salidaFolioFin.trim()) return warn("Rango de hologramas requerido", "Indique el folio inicial y final.");
    if (!isFoliosMatch) return warn("Descuadre en rango de hologramas", `El rango contiene ${foliosCount} folios y son ${botellasNum} botellas. Deben coincidir.`);

    const nuevo = {
      key: `n-${Date.now()}-${draftRangos.length}`,
      nuevo: true,
      id_bitacora_hologramas: boundsSel.id,
      idInventario: boundsSel.id,
      prefijo: salidaPrefijo,
      rango1: salidaFolioIni.trim(),
      rango2: salidaFolioFin.trim(),
      cantidad: botellasNum,
      rangoHologramas: `${salidaPrefijo}${salidaFolioIni.trim()} - ${salidaPrefijo}${salidaFolioFin.trim()}`,
      observaciones: salidaObs.trim(),
      estatus: "pendiente",
      salidas: []
    };
    const error = validarRangosSalida([nuevo], entradaBotModal, draftRangos);
    if (error) return warn("Revise el rango de hologramas", error);

    setDraftRangos(prev => [...prev, nuevo]);
    limpiarFormularioRango();
  };

  const handleQuitarRangoDraft = (key) => setDraftRangos(prev => prev.filter(d => d.key !== key));
  const handleCambiarDraft = (key, cambios) => setDraftRangos(prev => prev.map(d => (d.key === key ? { ...d, ...cambios } : d)));

  const handleGuardarSalidas = async () => {
    if (!selectedRecordForSalida || guardandoSalida) return;
    const warn = (title, text) => Swal.fire({ icon: "warning", title, text, confirmButtonColor: "#6B21A8" });

    if (salidaInvId && botellasNum > 0) {
      return warn("Rango sin agregar", "Tiene un rango capturado que no se ha agregado. Presione «Agregar rango» o límpielo antes de guardar.");
    }
    for (const d of draftRangos) {
      const errorSalidas = validarSalidasDeRango(d);
      if (errorSalidas) return warn("Revise las salidas", errorSalidas);
    }
    const nuevos = draftRangos.filter(d => d.nuevo);
    if (nuevos.length > 0) {
      const error = validarRangosSalida(nuevos, entradaBotModal, draftRangos.filter(d => !d.nuevo));
      if (error) return warn("Revise los rangos de hologramas", error);
    }

    const inv = calcRecordInventory(selectedRecordForSalida);
    const todos = draftRangos.map(({ key, nuevo, ...r }) => r);
    const resumen = resumenSalidasDesdeRangos(todos, {
      entradaBot: inv.entradaBot,
      entradaCaj: inv.entradaCaj,
      botPorCaja: selectedRecordForSalida.botCaja || selectedRecordForSalida.bot_caja
    });

    setGuardandoSalida(true);
    const resultado = onUpdateTerminado
      ? await onUpdateTerminado({ ...selectedRecordForSalida, ...resumen, rangosHologramas: todos })
      : { ok: true };
    if (resultado && resultado.ok !== false) {
      const loteActualizado = resultado.data || { ...selectedRecordForSalida, rangosHologramas: todos };
      await subirDocumentosPendientesDeRangos(draftRangos, loteActualizado);
      await subirDocumentosPendientesDeSalidas(draftRangos, loteActualizado);
    }
    setGuardandoSalida(false);

    if (resultado && resultado.ok === false) {
      Swal.fire({ icon: "error", title: "No se pudieron guardar los cambios", text: String(resultado.error), confirmButtonColor: "#DC2626" });
      return;
    }

    setIsSalidaModalOpen(false);
    setSelectedRecord(null);
    Swal.fire({
      icon: "success",
      title: "Salidas guardadas",
      html: `Los rangos y salidas del lote quedaron registrados.<br/><br/>Lo nuevo o modificado queda <strong>en revisión</strong> de la Unidad de Inspección, que acepta o rechaza cada rango y cada salida.<br/><strong>Botellas sin asignar:</strong> ${Math.max(0, inv.entradaBot - todos.filter(r => String(r.estatus || "").toLowerCase() !== "rechazado").reduce((acc, r) => acc + (parseInt(r.cantidad, 10) || 0), 0))}.`,
      confirmButtonColor: "#6B21A8"
    });
  };

  return (
    <div className="animate-fadeIn">
      <div className="history-section-header">
        <div>
          <h2>Inventarios de Mezcal Envasado "Terminado" (Sección IV)</h2>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "13px" }}>
            Control de inventarios de producto terminado, asignación de hologramas y registro de salidas parciales o totales (Venta Nacional, Exportación, Traslados o Bajas).
          </p>
        </div>
      </div>

      <div className="records-list-wrapper">
        <table className="records-table">
          <thead>
            <tr>
              <th>Fecha Mov.</th>
              <th>Bot/Caja</th>
              <th>Marca</th>
              <th>Lote Granel</th>
              <th>Lote Env.</th>
              <th style={{ background: "rgba(107, 33, 168, 0.05)", color: "#6B21A8" }}>Entrada (Bot/Caj)</th>
              <th style={{ background: "rgba(217, 119, 6, 0.05)", color: "#D97706" }}>Salidas (Bot/Caj)</th>
              <th style={{ background: "rgba(5, 150, 105, 0.05)", color: "#059669" }}>Saldo Disp.</th>
              <th>V.Bo. Inspector</th>
              <th>Estado</th>
              <th style={{ textAlign: "center", minWidth: "210px" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {terminadoRecords.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: "center", padding: "30px", color: "var(--text-muted)" }}>
                  No hay registros de producto terminado en este momento.
                </td>
              </tr>
            ) : (
              terminadoRecords.map((rec, i) => {
                const rangos = rec.rangosHologramas || [];
                const tieneHologramas = rangos.length > 0;
                const statusNormal = String(rec.status || rec.estatus || "revision").toLowerCase();
                const inv = calcRecordInventory(rec);
                const puedeAbrirSalidas = inv.saldoBot > 0 || (rec.rangosHologramas || []).length > 0;

                return (
                  <tr key={i}>
                    <td>{rec.fechaMovimiento || rec.fecha_movimiento}</td>
                    <td>{rec.botCaja || rec.bot_caja || "12 bot."}</td>
                    <td style={{ fontWeight: 800 }}>{rec.marca}</td>
                    <td>{rec.noLoteGranel || rec.no_lote_granel}</td>
                    <td style={{ color: "var(--copper)", fontWeight: 700 }}>{rec.loteEnvasado || rec.lote_envasado}</td>

                    {/* ENTRADA */}
                    <td style={{ fontWeight: 700, color: "#475569", background: "rgba(107, 33, 168, 0.02)" }}>
                      <div>{inv.entradaBot} bot.</div>
                      <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>({inv.entradaCaj} cajas)</div>
                    </td>

                    {/* SALIDAS Y PARCIALIDADES */}
                    <td style={{ background: "rgba(217, 119, 6, 0.02)" }}>
                      <div style={{ fontWeight: 700, color: inv.salidaBot > 0 ? "#D97706" : "var(--text-muted)" }}>
                        {inv.salidaBot} bot.
                      </div>
                      <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                        ({inv.salidaCaj} caj. | {inv.parcialidades.length} rango{inv.parcialidades.length === 1 ? '' : 's'})
                      </div>
                    </td>

                    {/* SALDO DISPONIBLE */}
                    <td style={{ background: "rgba(5, 150, 105, 0.02)" }}>
                      {inv.saldoBot > 0 ? (
                        <div style={{ display: "inline-block", background: "rgba(5, 150, 105, 0.12)", color: "#065F46", padding: "3px 8px", borderRadius: "6px", fontWeight: 800, fontSize: "12px" }}>
                          {inv.saldoBot} bot.
                          <span style={{ fontSize: "10.5px", fontWeight: 600, display: "block", color: "#047857" }}>
                            {inv.saldoCaj} cajas
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: "#DC2626", background: "#FEF2F2", border: "1px solid #FECACA", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: 700 }}>
                          Agotado (0)
                        </span>
                      )}
                    </td>

                    <td style={{ fontSize: "11px" }}>{rec.inspector || "Pendiente de V.Bo."}</td>
                    <td>
                      <span className={`badge-status ${statusNormal}`}>
                        {statusNormal === "aprobado" ? "Aprobado" : statusNormal === "revision" ? "En revisión" : statusNormal === "rechazado" ? "Rechazado" : "Borrador"}
                      </span>
                    </td>

                    {/* ACCIONES */}
                    <td>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>

                        {/* BOTÓN DAR SALIDA (HABILITADO AUNQUE ESTÉ APROBADO) */}
                        <button
                          type="button"
                          onClick={() => handleOpenSalidaModal(rec)}
                          disabled={!puedeAbrirSalidas}
                          style={{
                            background: puedeAbrirSalidas ? "linear-gradient(135deg, #6B21A8 0%, #9333EA 100%)" : "#E2E8F0",
                            color: puedeAbrirSalidas ? "#FFFFFF" : "#94A3B8",
                            border: "none",
                            borderRadius: "6px",
                            padding: "5px 10px",
                            fontSize: "11.5px",
                            fontWeight: 700,
                            cursor: puedeAbrirSalidas ? "pointer" : "not-allowed",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            boxShadow: puedeAbrirSalidas ? "0 2px 4px rgba(107, 33, 168, 0.25)" : "none",
                            transition: "all 0.2s ease"
                          }}
                          title={puedeAbrirSalidas ? "Agregar rangos y salidas, o editar las salidas de este lote" : "Lote sin botellas disponibles en inventario"}
                        >
                          <LocalShippingIcon style={{ fontSize: 14 }} />
                          <span>Agregar salida</span>
                        </button>

                        {/* BOTÓN VER DETALLE */}
                        <button
                          type="button"
                          onClick={() => setSelectedRecord(rec)}
                          style={{
                            background: "#F1F5F9",
                            border: "1px solid #CBD5E1",
                            color: "#475569",
                            borderRadius: "6px",
                            padding: "5px 8px",
                            fontSize: "11px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "3px"
                          }}
                          title="Ver detalle del lote e historial de parcialidades"
                        >
                          <VisibilityIcon style={{ fontSize: 14 }} />
                          <span>Detalle</span>
                        </button>

                        {/* BOTÓN DOCUMENTOS POR RANGO */}
                        {tieneHologramas && (
                          <button
                            type="button"
                            onClick={() => setRangoDocsModal({
                              isOpen: true,
                              rango: rangos[0],
                              lote: rec,
                              rangosDelLote: rangos
                            })}
                            style={{
                              background: "#F0FDF4",
                              border: "1px solid #86EFAC",
                              color: "#15803D",
                              borderRadius: "6px",
                              padding: "5px 8px",
                              fontSize: "11px",
                              fontWeight: 700,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px"
                            }}
                            title="Expediente de documentos por rango de hologramas (Certificado de Producto Envasado, Informe, etc.)"
                          >
                            <FolderSharedIcon style={{ fontSize: 14 }} />
                            <span>Docs</span>
                          </button>
                        )}

                        {statusNormal === "rechazado" && rec.observaciones_correccion && (
                          <button
                            type="button"
                            onClick={() => {
                              Swal.fire({
                                icon: "warning",
                                title: "Observaciones de Rechazo",
                                html: `<div style="text-align:left; font-size:13px; line-height:1.5; background:#FEF2F2; border:1px solid #FECACA; padding:12px; border-radius:8px; color:#991B1B;"><strong>Motivo indicado por la UI:</strong><br/><br/>${rec.observaciones_correccion}</div>`,
                                confirmButtonColor: "#DC2626"
                              });
                            }}
                            style={{
                              background: "#FFFBEB",
                              border: "1px solid #FCD34D",
                              color: "#D97706",
                              borderRadius: "6px",
                              padding: "5px 8px",
                              fontSize: "11px",
                              fontWeight: 700,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "3px"
                            }}
                            title="Ver observaciones del inspector"
                          >
                            <ReportProblemIcon style={{ fontSize: 13 }} />
                            <span>Motivo</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* =========================================================================
            MODAL AGREGAR SALIDA: RANGOS DEL LOTE + SALIDAS OPCIONALES POR RANGO
          ========================================================================= */}
      {isSalidaModalOpen && selectedRecordForSalida && (() => {
        const campo = { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1.5px solid #D8B4FE", fontSize: "13px", fontWeight: 600, background: "#fff", boxSizing: "border-box" };
        const etiqueta = { display: "block", fontSize: "11.5px", fontWeight: 700, color: "#6B21A8", marginBottom: 3 };
        const estilosEstatus = {
          aprobado: { fondo: "#ECFDF5", color: "#065F46", borde: "#A7F3D0", texto: "Aceptado" },
          rechazado: { fondo: "#FEF2F2", color: "#DC2626", borde: "#FECACA", texto: "Rechazado" },
          pendiente: { fondo: "#FFFBEB", color: "#B45309", borde: "#FDE68A", texto: "En revisión" }
        };
        const puedeAgregar = boundsSel && isFoliosMatch && botellasNum <= saldoDraft && botellasNum <= dispSel;

        return createPortal(
          <div className="custom-modal-overlay" style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15, 23, 42, 0.65)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100000, padding: "20px" }}>
            <div
              className="custom-modal-content animate-scaleUp"
              style={{ maxWidth: "860px", width: "96%", maxHeight: "92vh", overflowY: "auto", borderRadius: "14px", background: "#FFFFFF", padding: "24px", boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <div>
                  <h3 style={{ margin: 0, color: "#6B21A8", fontSize: "17px" }}>Agregar salida</h3>
                  <div style={{ fontSize: "12.5px", color: "#64748B", marginTop: 3 }}>
                    Lote <strong>{selectedRecordForSalida.loteEnvasado || selectedRecordForSalida.lote_envasado}</strong> — {selectedRecordForSalida.marca}
                  </div>
                </div>
                <button type="button" onClick={() => setIsSalidaModalOpen(false)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#64748B" }}>
                  <CloseIcon />
                </button>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", background: "#FAF5FF", border: "1px solid #E9D5FF", borderRadius: 8, padding: "10px 12px", fontSize: "12.5px", color: "#581C87", fontWeight: 600, marginBottom: 14 }}>
                <span>Botellas del lote: <strong>{entradaBotModal}</strong></span>
                <span>Asignadas a rangos: <strong>{asignadoDraft}</strong></span>
                <span>Sin asignar: <strong>{saldoDraft}</strong></span>
              </div>

              {/* RANGOS DEL LOTE */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {draftRangos.length === 0 && (
                  <div style={{ textAlign: "center", padding: 16, border: "1px dashed #CBD5E1", borderRadius: 8, color: "#64748B", fontSize: "12.5px" }}>
                    Este lote aún no tiene rangos de hologramas. Agregue el primero abajo.
                  </div>
                )}
                {draftRangos.map((d, i) => {
                  const est = estilosEstatus[String(d.estatus || "pendiente").toLowerCase()] || estilosEstatus.pendiente;
                  const aprobado = String(d.estatus || "").toLowerCase() === "aprobado";
                  return (
                    <div key={d.key} style={{ border: "1px solid #D8B4FE", borderRadius: 10, padding: 12, background: "#fff" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: "11px", fontWeight: 700, background: "#F3E8FF", color: "#6B21A8", border: "1px solid #E9D5FF" }}>Rango #{i + 1}</span>
                          <strong style={{ color: "#6B21A8", fontSize: "13px" }}>{d.rangoHologramas || `${d.prefijo}${d.rango1} - ${d.prefijo}${d.rango2}`}</strong>
                          <strong style={{ color: "#B45309", fontSize: "12.5px" }}>{d.cantidad} botellas</strong>
                          {d.nuevo ? (
                            <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: "11px", fontWeight: 700, background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }}>Nuevo</span>
                          ) : (
                            <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: "11px", fontWeight: 700, background: est.fondo, color: est.color, border: `1px solid ${est.borde}` }}>{est.texto}</span>
                          )}
                        </div>
                        {!aprobado && (
                          <button type="button" onClick={() => handleQuitarRangoDraft(d.key)} title="Quitar este rango" style={{ padding: "3px 10px", background: "#fff", color: "#DC2626", border: "1px solid #FECACA", borderRadius: 6, fontSize: "11.5px", fontWeight: 700, cursor: "pointer" }}>
                            Quitar rango
                          </button>
                        )}
                      </div>

                      {String(d.estatus).toLowerCase() === "rechazado" && d.observaciones_correccion && (
                        <div style={{ marginTop: 6, color: "#DC2626", fontSize: "11.5px" }}><strong>Observación del inspector:</strong> {d.observaciones_correccion}</div>
                      )}

                      <label style={{ ...etiqueta, marginTop: 8 }}>Observaciones del rango (opcional)
                        <textarea value={d.observaciones || ""} rows={1} onChange={e => handleCambiarDraft(d.key, { observaciones: e.target.value })} style={{ ...campo, resize: "vertical", fontWeight: 500 }} />
                      </label>

                      <SalidasDelRango
                        rango={d}
                        salidas={d.salidas || []}
                        almacenes={almacenes}
                        onChange={lista => handleCambiarDraft(d.key, { salidas: lista })}
                        onOpenDocsSalida={(sal, rg, listaSalidas) => setSalidaDocsModal({
                          isOpen: true,
                          salida: sal,
                          rango: rg,
                          lote: selectedRecordForSalida,
                          salidasDelRango: (listaSalidas || []).filter(s => s.id_salida || s.id)
                        })}
                      />

                      <DocumentosDelRango
                        rango={d}
                        onChange={next => handleCambiarDraft(d.key, { documentosPendientes: next.documentosPendientes })}
                        onOpenModal={rg => setRangoDocsModal({
                          isOpen: true,
                          rango: rg,
                          lote: selectedRecordForSalida,
                          rangosDelLote: draftRangos.filter(x => !x.nuevo && (x.id_producto_holograma || x.id))
                        })}
                        origen="envasado_terminado"
                      />
                    </div>
                  );
                })}
              </div>

              {/* AGREGAR OTRO RANGO */}
              <div style={{ marginTop: 16, padding: 12, border: "1px dashed #D8B4FE", borderRadius: 10, background: "#FDFAFF" }}>
                <div style={{ fontSize: "13px", fontWeight: 800, color: "#6B21A8", marginBottom: 8 }}>Agregar rango de hologramas</div>
                {saldoDraft <= 0 ? (
                  <div style={{ fontSize: "12.5px", color: "#64748B" }}>Las botellas del lote ya están asignadas a rangos. Quite un rango para poder agregar otro.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div>
                      <label style={etiqueta}>Inventario de hologramas disponibles *</label>
                      <select value={salidaInvId} onChange={e => handleSelectInventario(e.target.value)} style={campo}>
                        <option value="">
                          {inventariosConSaldo.length > 0 ? "-- Seleccionar lote de inventario --" : "-- No hay inventario de hologramas con saldo --"}
                        </option>
                        {inventariosConSaldo.map(b => (
                          <option key={b.id} value={b.id}>[{b.prefijo}] {b.rangoCompleto} — Disponible: {Math.max(0, b.disponible - usadoNuevoInv(b.id))} pzas</option>
                        ))}
                      </select>
                    </div>

                    {salidaInvId && (
                      <>
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) 70px 1fr 1fr", gap: 8, alignItems: "end" }}>
                          <div>
                            <label style={etiqueta}>Botellas * (máx. {saldoDraft})</label>
                            <input type="number" min="1" max={saldoDraft} value={salidaBotellas} onChange={e => handleSalidaBotellasChange(e.target.value)} style={campo} />
                          </div>
                          <div>
                            <label style={etiqueta}>Prefijo</label>
                            <input type="text" value={salidaPrefijo} readOnly style={{ ...campo, textAlign: "center", background: "#F8FAFC" }} />
                          </div>
                          <div>
                            <label style={etiqueta}>Folio inicial *</label>
                            <input type="text" value={salidaFolioIni} onChange={e => handleFolioIniChange(e.target.value)} style={campo} />
                          </div>
                          <div>
                            <label style={etiqueta}>Folio final *</label>
                            <input type="text" value={salidaFolioFin} onChange={e => setSalidaFolioFin(e.target.value)} style={campo} />
                          </div>
                        </div>
                        {botellasNum > 0 && salidaFolioIni && salidaFolioFin && (
                          <div style={{ fontSize: "12px", fontWeight: 600, color: isFoliosMatch ? "#065F46" : "#DC2626" }}>
                            {isFoliosMatch
                              ? `✓ ${foliosCount} hologramas para ${botellasNum} botellas.`
                              : `⚠️ Folios en rango: ${foliosCount} | Botellas: ${botellasNum} (deben coincidir).`}
                          </div>
                        )}
                        <div>
                          <label style={etiqueta}>Observaciones del rango (opcional)</label>
                          <textarea value={salidaObs} onChange={e => setSalidaObs(e.target.value)} rows={1} placeholder="Observaciones de este rango" style={{ ...campo, resize: "vertical", fontWeight: 500 }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            onClick={handleAgregarRangoDraft}
                            disabled={!puedeAgregar}
                            style={{ padding: "7px 14px", background: puedeAgregar ? "#fff" : "#F1F5F9", color: "#6B21A8", border: "1px solid #D8B4FE", borderRadius: 8, fontWeight: 700, fontSize: "12.5px", cursor: puedeAgregar ? "pointer" : "not-allowed" }}
                          >
                            + Agregar rango
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={() => setIsSalidaModalOpen(false)} className="btn-secondary-custom" style={{ padding: "8px 16px" }}>
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleGuardarSalidas}
                  disabled={guardandoSalida}
                  style={{
                    background: guardandoSalida ? "#CBD5E1" : "linear-gradient(135deg, #6B21A8 0%, #9333EA 100%)",
                    color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 700, fontSize: "13px",
                    cursor: guardandoSalida ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6
                  }}
                >
                  <LocalShippingIcon style={{ fontSize: 16 }} />
                  <span>{guardandoSalida ? "Guardando..." : "Guardar rangos y salidas"}</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* =========================================================================
            MODAL DETALLE DE LOTE TERMINADO CON HISTORIAL DE PARCIALIDADES
          ========================================================================= */}
      {selectedRecord && (() => {
        const inv = calcRecordInventory(selectedRecord);
        const parcialidades = inv.parcialidades;
        const statusNormal = String(selectedRecord.status || selectedRecord.estatus || "revision").toLowerCase();

        return createPortal(
          <div className="custom-modal-overlay" style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15, 23, 42, 0.65)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100000, padding: "20px" }}>
            <div
              className="custom-modal-content animate-scaleUp"
              style={{ maxWidth: "750px", width: "95%", maxHeight: "90vh", overflowY: "auto", borderRadius: "14px", background: "#fff", padding: "24px" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E2E8F0", paddingBottom: "12px", marginBottom: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ background: "rgba(107, 33, 168, 0.1)", color: "#6B21A8", padding: "8px", borderRadius: "8px" }}>
                    <WorkspacePremiumIcon />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>Detalle de Lote Terminado</h3>
                    <p style={{ margin: 0, fontSize: "12px", color: "var(--text-muted)" }}>
                      Lote {selectedRecord.loteEnvasado || selectedRecord.lote_envasado} — {selectedRecord.marca}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedRecord(null)}
                  style={{ background: "transparent", border: "none", cursor: "pointer", color: "#64748B" }}
                >
                  <CloseIcon />
                </button>
              </div>

              {/* Balance de Inventario */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginBottom: "16px" }}>
                <div style={{ padding: "10px 14px", background: "#F8FAFC", borderRadius: "8px", border: "1px solid #E2E8F0" }}>
                  <span style={{ fontSize: "11px", color: "#64748B", fontWeight: 600 }}>Entrada Total:</span>
                  <div style={{ fontSize: "15px", fontWeight: 800, color: "#1E293B" }}>{inv.entradaBot} botellas</div>
                  <span style={{ fontSize: "11px", color: "#64748B" }}>({inv.entradaCaj} cajas)</span>
                </div>

                <div style={{ padding: "10px 14px", background: "#FFFBEB", borderRadius: "8px", border: "1px solid #FCD34D" }}>
                  <span style={{ fontSize: "11px", color: "#92400E", fontWeight: 600 }}>Salidas Totales:</span>
                  <div style={{ fontSize: "15px", fontWeight: 800, color: "#B45309" }}>{inv.salidaBot} botellas</div>
                  <span style={{ fontSize: "11px", color: "#92400E" }}>({inv.salidaCaj} cajas)</span>
                </div>

                <div style={{ padding: "10px 14px", background: "#ECFDF5", borderRadius: "8px", border: "1px solid #6EE7B7" }}>
                  <span style={{ fontSize: "11px", color: "#065F46", fontWeight: 600 }}>Saldo en Almacén:</span>
                  <div style={{ fontSize: "15px", fontWeight: 800, color: "#047857" }}>{inv.saldoBot} botellas</div>
                  <span style={{ fontSize: "11px", color: "#047857" }}>({inv.saldoCaj} cajas)</span>
                </div>
              </div>

              {/* Grid de Datos del Lote */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", fontSize: "12.5px" }}>
                <div><strong>Fecha de Movimiento:</strong> {selectedRecord.fechaMovimiento || selectedRecord.fecha_movimiento}</div>
                <div><strong>Marca:</strong> {selectedRecord.marca}</div>
                <div><strong>Lote a Granel:</strong> {selectedRecord.noLoteGranel || selectedRecord.no_lote_granel}</div>
                <div><strong>Lote Envasado:</strong> {selectedRecord.loteEnvasado || selectedRecord.lote_envasado}</div>
                <div><strong>Categoría:</strong> {selectedRecord.categoria}</div>
                <div><strong>Clase:</strong> {selectedRecord.clase || selectedRecord.claseEtiqueta || "-"}</div>
                <div><strong>Clase Etiqueta:</strong> {selectedRecord.claseEtiqueta || selectedRecord.clase || "-"}</div>
                <div><strong>Tipo de Maguey:</strong> {selectedRecord.tipoMagueyEtiqueta || selectedRecord.tipoMaguey || "Espadín"}</div>
                <div><strong>Capacidad:</strong> {selectedRecord.capacidad || "750 ml"}</div>
                <div><strong>% Alc. Vol.:</strong> {selectedRecord.pctAlcEtiqueta || selectedRecord.pctAlc || "-"}</div>
                <div><strong>No. Informe Aprobación:</strong> {selectedRecord.noInformeAprobacion || "-"}</div>
                <div><strong>Estatus:</strong> <span className={`badge-status ${statusNormal}`}>{statusNormal}</span></div>
                <div><strong>Inspector:</strong> {selectedRecord.inspector || "Pendiente de V.Bo."}</div>
              </div>

              {/* Observaciones de Dictamen */}
              {selectedRecord.observaciones_correccion && (
                <div style={{ marginTop: "14px", padding: "12px 14px", background: "#FEF2F2", border: "1.5px solid #FCA5A5", borderRadius: "8px", color: "#991B1B", fontSize: "12.5px" }}>
                  <strong style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                    <ReportProblemIcon style={{ fontSize: 16 }} />
                    Observaciones de Corrección / Dictamen de la UI:
                  </strong>
                  <span>{selectedRecord.observaciones_correccion}</span>
                </div>
              )}

              {/* Rangos de Hologramas Asignados */}
              <div style={{ marginTop: "16px", padding: "12px", background: "#FAF5FF", borderRadius: "8px", border: "1px solid #E9D5FF" }}>
                <h4 style={{ margin: "0 0 8px 0", color: "#6B21A8", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <ConfirmationNumberIcon style={{ fontSize: 16 }} />
                  Rangos de Hologramas Asignados al Lote
                </h4>
                {(selectedRecord.rangosHologramas || []).length === 0 ? (
                  <p style={{ margin: 0, fontSize: "12px", color: "#6B21A8" }}>No se registraron folios específicos.</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {selectedRecord.rangosHologramas.map((r, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", background: "#fff", padding: "6px 10px", borderRadius: "6px", border: "1px solid #D8B4FE" }}>
                        <span><strong>Rango:</strong> {r.rangoHologramas || `${r.prefijo || 'A'}${r.rango1 || r.rango_inicial} - ${r.prefijo || 'A'}${r.rango2 || r.rango_final}`}</span>
                        <span><strong>Cantidad:</strong> {r.cantidad || r.cantidadInicial || "0"} folios</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* HISTORIAL DE SALIDAS PARCIALES */}
              <div style={{ marginTop: "18px", padding: "14px", background: "#F8FAFC", borderRadius: "10px", border: "1px solid #E2E8F0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", flexWrap: "wrap", gap: "8px" }}>
                  <h4 style={{ margin: 0, fontSize: "13.5px", fontWeight: 700, color: "#1E293B", display: "flex", alignItems: "center", gap: "6px" }}>
                    <HistoryIcon style={{ fontSize: 18, color: "#D97706" }} />
                    Historial de Salidas Parciales y Movimientos ({parcialidades.length})
                  </h4>
                  {(inv.saldoBot > 0 || parcialidades.length > 0) && (
                    <button
                      type="button"
                      onClick={() => handleOpenSalidaModal(selectedRecord)}
                      style={{
                        padding: "5px 12px",
                        background: "linear-gradient(135deg, #6B21A8 0%, #9333EA 100%)",
                        color: "#FFFFFF",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "11.5px",
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                    >
                      <LocalShippingIcon style={{ fontSize: 13 }} />
                      <span>Agregar / editar salidas</span>
                    </button>
                  )}
                </div>

                {parcialidades.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "20px", background: "#FFFFFF", borderRadius: "8px", border: "1px dashed #CBD5E1", color: "#64748B", fontSize: "12px" }}>
                    ℹ️ Aún no se han registrado salidas parciales de este lote. El lote completo ({inv.entradaBot} botellas) permanece disponible en almacén.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {parcialidades.map((parcial, pIndex) => {
                      const estilosEstatus = {
                        aprobado: { fondo: "#ECFDF5", color: "#065F46", borde: "#A7F3D0", texto: "Aceptado" },
                        rechazado: { fondo: "#FEF2F2", color: "#DC2626", borde: "#FECACA", texto: "Rechazado" },
                        pendiente: { fondo: "#FFFBEB", color: "#B45309", borde: "#FDE68A", texto: "En revisión" },
                        revision: { fondo: "#FFFBEB", color: "#B45309", borde: "#FDE68A", texto: "En revisión" }
                      };
                      const est = estilosEstatus[parcial.estatus] || estilosEstatus.pendiente;
                      const asignadoSalidas = parcial.salidas.filter(x => x.estatus !== "rechazado").reduce((acc, x) => acc + (parseInt(x.cantidad, 10) || 0), 0);
                      return (
                        <div
                          key={parcial.id || pIndex}
                          style={{ background: "#FFFFFF", border: "1px solid #CBD5E1", borderRadius: "8px", padding: "10px 12px", display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px" }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
                            <span style={{ padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 700, background: "#F3E8FF", color: "#6B21A8", border: "1px solid #E9D5FF" }}>
                              Rango #{pIndex + 1}
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              {(parcial.id_producto_holograma || parcial.id) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const fullRango = (selectedRecord.rangosHologramas || []).find(
                                      r => String(r.id_producto_holograma || r.id) === String(parcial.id_producto_holograma || parcial.id)
                                    ) || parcial;
                                    setRangoDocsModal({
                                      isOpen: true,
                                      rango: fullRango,
                                      lote: selectedRecord,
                                      rangosDelLote: selectedRecord.rangosHologramas || []
                                    });
                                  }}
                                  style={{
                                    padding: "3px 9px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    background: "#F0FDF4",
                                    color: "#15803D",
                                    border: "1px solid #86EFAC",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px"
                                  }}
                                >
                                  <FolderSharedIcon style={{ fontSize: 13 }} />
                                  <span>Documentos del Rango</span>
                                </button>
                              )}
                              <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 700, background: est.fondo, color: est.color, border: `1px solid ${est.borde}` }}>
                                {est.texto}
                              </span>
                            </div>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", background: "#FAF5FF", padding: "4px 8px", borderRadius: "4px", border: "1px solid #E9D5FF", color: "#6B21A8" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <ConfirmationNumberIcon style={{ fontSize: 14 }} />
                              <span><strong>Hologramas:</strong> {parcial.rangoHologramas}</span>
                            </span>
                            <strong style={{ color: "#B45309" }}>{parcial.botellas} botellas</strong>
                          </div>

                          {parcial.observaciones && (
                            <div><strong>Observaciones del rango:</strong> {parcial.observaciones}</div>
                          )}

                          {parcial.salidas.length === 0 ? (
                            <div style={{ color: "#64748B" }}>Sin salidas definidas para este rango (opcional).</div>
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 10, borderLeft: "3px solid #E9D5FF" }}>
                              {parcial.salidas.map((x, sIndex) => {
                                const estX = estilosEstatus[x.estatus] || estilosEstatus.pendiente;
                                const salDocsCount = Array.isArray(x.documentos) ? x.documentos.length : (x.total_documentos || 0);
                                return (
                                  <div key={x.id_salida || sIndex} style={{ display: "flex", flexDirection: "column", gap: 4, background: "#F8FAFC", borderRadius: 6, padding: "6px 10px", border: "1px solid #E2E8F0" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                                      <span>
                                        <strong>{x.tipo_salida || "Sin tipo de salida"}</strong> · {x.rango_completo || x.rangoHologramas} · <strong style={{ color: "#B45309" }}>{x.cantidad} botellas</strong>
                                      </span>
                                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        {(x.id_salida || x.id) && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const fullRango = (selectedRecord.rangosHologramas || []).find(
                                                r => String(r.id_producto_holograma || r.id) === String(parcial.id_producto_holograma || parcial.id)
                                              ) || parcial;
                                              setSalidaDocsModal({
                                                isOpen: true,
                                                salida: x,
                                                rango: fullRango,
                                                lote: selectedRecord,
                                                salidasDelRango: fullRango.salidas || parcial.salidas || []
                                              });
                                            }}
                                            style={{
                                              padding: "2px 8px",
                                              borderRadius: "6px",
                                              fontSize: "10.5px",
                                              fontWeight: 700,
                                              background: "#E0F2FE",
                                              color: "#0369A1",
                                              border: "1px solid #7DD3FC",
                                              cursor: "pointer",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "4px"
                                            }}
                                            title="Ver o adjuntar documentos exclusivos de esta salida (Certificado de Producto Envasado, Informe u Otros Documentos)"
                                          >
                                            <FolderSharedIcon style={{ fontSize: 12 }} />
                                            <span>Documentos de la Salida ({salDocsCount})</span>
                                          </button>
                                        )}
                                        <span style={{ padding: "1px 8px", borderRadius: "10px", fontSize: "10.5px", fontWeight: 700, background: estX.fondo, color: estX.color, border: `1px solid ${estX.borde}` }}>{estX.texto}</span>
                                      </div>
                                    </div>
                                    {x.observaciones && <span style={{ color: "#475569" }}>“{x.observaciones}”</span>}
                                    {x.estatus === "rechazado" && x.observaciones_correccion && (
                                      <span style={{ color: "#DC2626" }}><strong>Observación del inspector:</strong> {x.observaciones_correccion}</span>
                                    )}
                                  </div>
                                );
                              })}
                              {asignadoSalidas < parcial.botellas && (
                                <span style={{ color: "#64748B", fontSize: "11.5px" }}>{parcial.botellas - asignadoSalidas} folios de este rango sin salida definida.</span>
                              )}
                            </div>
                          )}

                          {parcial.estatus === "rechazado" && parcial.observacionesCorreccion && (
                            <div style={{ color: "#DC2626", fontSize: "11.5px" }}>
                              <strong>Observación del inspector:</strong> {parcial.observacionesCorreccion}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Botón Cerrar */}
              <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setSelectedRecord(null)}
                  className="btn-secondary-custom"
                  style={{ padding: "8px 16px" }}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* MODAL DE DOCUMENTOS POR RANGO DE HOLOGRAMAS */}
      <ModalDocumentosRangoHolograma
        isOpen={rangoDocsModal.isOpen}
        onClose={() => setRangoDocsModal({ isOpen: false, rango: null, lote: null, rangosDelLote: [] })}
        rango={rangoDocsModal.rango}
        lote={rangoDocsModal.lote}
        rangosDelLote={rangoDocsModal.rangosDelLote}
      />

      {/* MODAL DE DOCUMENTOS POR SALIDA DE HOLOGRAMAS */}
      <ModalDocumentosSalidaHolograma
        isOpen={salidaDocsModal.isOpen}
        onClose={() => setSalidaDocsModal({ isOpen: false, salida: null, rango: null, lote: null, salidasDelRango: [] })}
        salida={salidaDocsModal.salida}
        rango={salidaDocsModal.rango}
        lote={salidaDocsModal.lote}
        salidasDelRango={salidaDocsModal.salidasDelRango}
      />
    </div>
  );
};

export default EnvasadoTerminadoSection;
